import { addDays, addWeeks, format, parse, startOfWeek } from 'date-fns';
import {
  WeekReview,
  WeekReviewDayInput,
  WeekReviewEntry,
  WeeksReviewConfig,
} from 'src/app/analytics/state/analytics.interface';
import { secondsToTimeInHours, signedSecondsToTimeInHours } from './data-formatting';
import { calculateDayTotals } from './timestamp.utils';

const WEEK_KEY_FORMAT = 'yyyy-MM-dd';

function parseWeekKey(key: string): Date {
  return parse(key, WEEK_KEY_FORMAT, new Date());
}

/** Canonical week key: the yyyy-MM-dd of the week's Sunday start (date-fns default). */
export function weekKeyFor(date: Date): string {
  return format(startOfWeek(date), WEEK_KEY_FORMAT);
}

/** True only for a well-formed key that IS its own week's start. */
export function isCanonicalWeekKey(key: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) {
    return false;
  }
  const parsed = parseWeekKey(key);
  if (Number.isNaN(parsed.getTime())) {
    return false;
  }
  return weekKeyFor(parsed) === key;
}

/** "Aug 9 – 15" within a month, "Jul 26 – Aug 1" across month/year boundaries. */
export function weekLabel(weekKey: string): string {
  const start = parseWeekKey(weekKey);
  const end = addDays(start, 6);
  const sameMonth = start.getMonth() === end.getMonth();
  return `${format(start, 'MMM d')} – ${format(end, sameMonth ? 'd' : 'MMM d')}`;
}

/**
 * Assembles the weekly-review model: the current week ("so far", excluded from the balance)
 * plus the last `windowWeeks` COMPLETED weeks, clipped at the earliest week that has any
 * history (no phantom deficits before the user existed). Weeks inside the window with no
 * data count as a full deficit — exceptions are per-week overrides, by design.
 * `inProgressFocusSeconds` mirrors realtimeWeeklyAnalytics$'s today semantics (the running
 * focus session is excluded from the "so far" number).
 */
export function buildWeekReview(
  days: WeekReviewDayInput[],
  config: WeeksReviewConfig,
  now: Date,
  inProgressFocusSeconds: number,
): WeekReview {
  const { weeklyTargetMinutes, weekTargetOverrides, windowWeeks, dayStartHour } = config;
  // Defensive: the settings layer only stores canonical keys, but a malformed value must
  // degrade to rolling mode rather than corrupt the loop bounds below.
  const startWeekKey =
    config.startWeekKey !== null && isCanonicalWeekKey(config.startWeekKey)
      ? config.startWeekKey
      : null;

  const totalsByWeek = new Map<string, number>();
  let earliestWeekKey: string | null = null;
  for (const day of days) {
    const key = weekKeyFor(parseWeekKey(day.id));
    const totals = calculateDayTotals(day.timestamps, day.multiplier, dayStartHour);
    totalsByWeek.set(key, (totalsByWeek.get(key) ?? 0) + totals.total);
    if (earliestWeekKey === null || key < earliestWeekKey) {
      earliestWeekKey = key;
    }
  }

  const currentWeekStart = startOfWeek(now);
  const currentKey = format(currentWeekStart, WEEK_KEY_FORMAT);

  const makeEntry = (weekKey: string, isCurrentWeek: boolean): WeekReviewEntry => {
    let totalSeconds = totalsByWeek.get(weekKey) ?? 0;
    if (isCurrentWeek) {
      totalSeconds = Math.max(0, totalSeconds - inProgressFocusSeconds);
    }
    const targetMinutes = weekTargetOverrides[weekKey] ?? weeklyTargetMinutes;
    const deltaSeconds =
      !isCurrentWeek && targetMinutes !== null ? totalSeconds - targetMinutes * 60 : null;
    return {
      weekStart: weekKey,
      label: isCurrentWeek ? 'This week' : weekLabel(weekKey),
      totalSeconds,
      targetMinutes,
      overridden: weekKey in weekTargetOverrides,
      deltaSeconds,
      isCurrentWeek,
      totalFormatted: secondsToTimeInHours(totalSeconds),
      deltaFormatted: deltaSeconds === null ? null : signedSecondsToTimeInHours(deltaSeconds),
    };
  };

  const weeks: WeekReviewEntry[] = [makeEntry(currentKey, true)];
  // Anchored mode spans every completed week back to the start week; rolling mode spans
  // the window. Both stop at the history horizon (no phantom deficits before the user's
  // first data). The loop always terminates: weekKey strictly decreases each iteration.
  for (let i = 1; ; i++) {
    const weekKey = format(addWeeks(currentWeekStart, -i), WEEK_KEY_FORMAT);
    if (startWeekKey === null ? i > windowWeeks : weekKey < startWeekKey) {
      break;
    }
    if (earliestWeekKey === null || weekKey < earliestWeekKey) {
      break;
    }
    weeks.push(makeEntry(weekKey, false));
  }

  const completed = weeks.filter((week) => !week.isCurrentWeek);
  const balanceSeconds =
    weeklyTargetMinutes === null
      ? null
      : completed.reduce((sum, week) => sum + (week.deltaSeconds ?? 0), 0);

  return {
    weeks,
    summary: {
      balanceSeconds,
      balanceFormatted: balanceSeconds === null ? null : signedSecondsToTimeInHours(balanceSeconds),
      windowWeeks,
      weeksCounted: completed.length,
      defaultTargetMinutes: weeklyTargetMinutes,
      startWeekKey,
    },
  };
}
